import { accentText } from "../utils/accent";

/**
 * VerticalTabLayout — reusable vertical sidebar + content shell for drawer forms.
 * Renders sidebar + tab header. Consumer provides the content (scrollable + optional footer).
 *
 * Usage:
 *   const tabs = [
 *     { id: "dados", icon: ClipboardList, label: "Dados", subtitle: "...",
 *       badge: 3, action: <Button>+</Button> },
 *     ...
 *   ];
 *   <VerticalTabLayout tabs={tabs} activeTab={drawerTab} onChange={setDrawerTab} sidebarTitle="Serviço">
 *     <form onSubmit={...} className="flex-1 flex flex-col min-h-0">
 *       <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
 *         {drawerTab === "dados" && <DadosContent />}
 *       </div>
 *       {drawerTab === "dados" && <FooterButtons />}
 *     </form>
 *   </VerticalTabLayout>
 */
export default function VerticalTabLayout({
  tabs,              // Array<{id, icon, label, badge?, subtitle?, action?, hidden?, groupLabel?}>
  activeTab,
  onChange,
  sidebarTitle,      // Optional small label above the tabs
  sidebarHidden,     // Optional bool: hide the whole sidebar
  hideHeader,        // Optional bool: hide the tab header (e.g. when creating new record)
  children,          // Consumer-provided content below the header
  testIdPrefix = "tab",
}) {
  const visibleTabs = tabs.filter((t) => !t.hidden);
  const activeCfg = visibleTabs.find((t) => t.id === activeTab);

  return (
    <div className="flex flex-1 min-h-0">
      {!sidebarHidden && (
        <div className="w-14 md:w-[200px] shrink-0 bg-gray-50/80 border-r border-gray-200 p-2 md:p-3 space-y-1 overflow-y-auto">
          {sidebarTitle && (
            <div className="pb-1 px-1 hidden md:block">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{sidebarTitle}</span>
            </div>
          )}
          {visibleTabs.map((t) => (
            <div key={t.id}>
              {t.groupLabel && (
                <div className="pt-2 pb-1 px-1 hidden md:block">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{t.groupLabel}</span>
                </div>
              )}
              {t.disabled && t.disabledTooltip ? (
                <div className="relative group">
                  <TabBtn tab={t} active={false} onClick={() => {}} testIdPrefix={testIdPrefix} />
                  <span className="pointer-events-none absolute left-0 right-0 top-full mt-1 hidden group-hover:block whitespace-normal rounded-md bg-gray-900 px-2 py-1.5 text-[11px] leading-snug font-medium text-white shadow-lg z-[60]" data-testid={`${testIdPrefix}-${t.id}-tooltip`}>
                    {t.disabledTooltip}
                  </span>
                </div>
              ) : (
                <TabBtn tab={t} active={activeTab === t.id} onClick={() => !t.disabled && onChange(t.id)} testIdPrefix={testIdPrefix} />
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex-1 min-w-0 overflow-hidden flex flex-col">
        {!hideHeader && activeCfg && <TabHeader cfg={activeCfg} />}
        {children}
      </div>
    </div>
  );
}

function mostraBadge(badge) {
  // Badge aceita número (contagem) e texto curto (ex.: estado de validação).
  if (badge == null || badge === "") return false;
  return typeof badge === "number" ? badge > 0 : true;
}

const toneBadge = (tone) =>
  tone === "warn"
    ? "bg-amber-100 text-amber-700"
    : tone === "ok"
      ? "bg-emerald-100 text-emerald-700"
      : "bg-gray-100 text-gray-500";

function TabBtn({ tab, active, onClick, testIdPrefix }) {
  const Icon = tab.icon;
  const disabled = !!tab.disabled;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled && !tab.disabledTooltip}
      aria-disabled={disabled}
      data-testid={`${testIdPrefix}-${tab.id}`}
      title={disabled && tab.disabledTooltip ? undefined : disabled && tab.disabledHint ? tab.disabledHint : tab.label}
      className={`relative flex items-center justify-center md:justify-start gap-2 w-full px-2 md:px-3 py-2 text-xs font-medium rounded-lg transition-colors whitespace-nowrap border ${
        active
          ? "bg-white text-gray-900 border-gray-200 shadow-sm"
          : disabled
            ? `text-gray-300 border-transparent opacity-60 ${tab.disabledTooltip ? "cursor-default" : "cursor-not-allowed"}`
            : "text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 border-transparent"
      }`}
    >
      {Icon && <Icon size={14} className={active ? "" : disabled ? "text-gray-300" : "text-gray-400"} style={active ? accentText() : undefined} />}
      <span className="flex-1 text-left hidden md:inline">{tab.label}</span>
      {mostraBadge(tab.badge) && (
        // Telas estreitas: só o ícone, com o contador no canto.
        <span className={`text-[10px] leading-none rounded-full px-1.5 py-0 font-bold inline-flex items-center justify-center min-w-[16px] h-4 absolute -top-1 -right-1 md:static ${toneBadge(tab.badgeTone)}`}>{tab.badge}</span>
      )}
    </button>
  );
}

function TabHeader({ cfg }) {
  const Icon = cfg.icon;
  return (
    <div className="px-6 pt-5 pb-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {Icon && <Icon size={16} style={accentText()} />}
            <h3 className="text-sm font-semibold text-gray-900">{cfg.label}</h3>
            {mostraBadge(cfg.badge) && (
              <span className="bg-white text-gray-600 border border-gray-200 text-[11px] font-bold px-2 py-0 leading-none h-5 min-w-[20px] inline-flex items-center justify-center rounded-md select-none">{cfg.badge}</span>
            )}
          </div>
          {cfg.subtitle && <p className="text-xs text-gray-500 mt-1 leading-relaxed">{cfg.subtitle}</p>}
        </div>
        {cfg.action && <div className="shrink-0">{cfg.action}</div>}
      </div>
    </div>
  );
}
